import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';

/**
 * Deterministic boundary/time-of-day probes for the Last Updated date-range filter epic
 * (dl-63212, Testmo folder "dl-58030" — 18 cases proving the `lastupdated` filter compares
 * DATES not DATETIMEs). Pushed onto the real, mutable `itemsData` array in `items.ts` (not
 * injected per-test via `apiTransform`'s `data` override) because `apiTransform`'s `data`
 * REPLACES the handler's response wholesale (`mocks/helpers.ts` `response()`: `if
 * (transform.data) data = transform.data`), bypassing `applyFilters` entirely — verified
 * empirically while building the e2e specs (a synthetic `apiTransform` array is served as-is
 * regardless of the `lastupdated` query param, so it cannot exercise real server-side
 * filtering). These probes are real rows the handler's own `applyFilters`/`sortItems`
 * genuinely filter/sort.
 *
 * Kept in its own module (rather than inline in `items.ts`) so the shared handler file stays
 * under the repo's 800-line file cap — this block is purely additive fixture data with no
 * dependency on anything else `items.ts` declares locally.
 *
 * Dates are deliberately outside the generated 213-row dataset's coverage (which is always
 * year 2025, day-of-month 1–28 only — `i % 28 + 1` never reaches 29/30/31) and outside every
 * other named fixture's date (`2026-01-15T09:00:00`), so no probe here is ever accidentally
 * included/excluded by an unrelated row:
 *   - Year-END boundary (2025-12-31) and year-START boundary (2026-01-01) share their
 *     "just before / just after" neighbours (2025-12-31T23:59:59 is simultaneously "late in
 *     the day on the 2025-12-31 boundary" AND "just before the 2026-01-01 boundary"; likewise
 *     2026-01-01T00:00:00 is simultaneously "midnight on the 2026-01-01 boundary" AND "just
 *     after the 2025-12-31 boundary").
 *   - A separate 2026-02-08..13 cluster backs the same-date / from-only / to-only / full-range
 *     / to-inclusive (core fix) / from-inclusive (regression) tests, which need multiple
 *     distinct in-range and out-of-range days rather than a single boundary pair.
 *   - 2020-01-01 / 2030-01-01 back the "effectively unbounded" (From-only / To-only / literal
 *     9999-12-31 sentinel) assertions.
 */
export const lastUpdatedProbeIds = {
  yearEndBeforeBoundary: 6329001, // 2025-12-30T23:59:59 — just before the Dec 31 boundary
  yearEndMidnight: 6329002, // 2025-12-31T00:00:00 — on the Dec 31 boundary, midnight
  yearEndMidday: 6329003, // 2025-12-31T12:00:00 — on the Dec 31 boundary, midday
  yearEndLateAndYearStartBefore: 6329004, // 2025-12-31T23:59:59 — Dec 31 late-in-day AND "just before Jan 1"
  yearStartMidnightAndYearEndAfter: 6329005, // 2026-01-01T00:00:00 — Jan 1 midnight AND "just after Dec 31"
  yearStartMidday: 6329006, // 2026-01-01T12:00:00 — on the Jan 1 boundary, midday
  yearStartLate: 6329007, // 2026-01-01T23:59:59 — on the Jan 1 boundary, late in the day
  yearStartAfterBoundary: 6329008, // 2026-01-02T00:00:00 — just after the Jan 1 boundary
  midRangeBefore: 6329009, // 2026-02-07T23:59:59 — just before the Feb 8–12 range
  midRangeStart: 6329010, // 2026-02-08T00:00:00 — on the range's From boundary
  midRangeMiddle: 6329011, // 2026-02-10T12:00:00 — inside the range, midday
  midDayEarly: 6329012, // 2026-02-10T00:00:01 — Feb 10, just after midnight (From-inclusive proof)
  midDayLate: 6329013, // 2026-02-10T23:59:59 — Feb 10, end of day (To-inclusive / core-fix proof)
  midRangeEnd: 6329014, // 2026-02-12T23:59:59 — on the range's To boundary, end of day
  midRangeAfter: 6329015, // 2026-02-13T00:00:00 — just after the Feb 8–12 range
  midDayAfter: 6329016, // 2026-02-11T00:00:00 — the day after the Feb 10 same-date test
  farPast: 6329017, // 2020-01-01T00:00:00 — proves an unbounded lower end truly has no floor
  farFuture: 6329018, // 2030-01-01T00:00:00 — proves an unbounded upper end truly has no ceiling
} as const;

export type LastUpdatedProbeItem = {
  id: number;
  name: string;
  userDefinedId: string;
  description: null;
  brand: (typeof brandOptions)[number];
  organization: (typeof organizations)[number];
  supplier: (typeof suppliers)[number];
  companyContactPerson: (typeof contactPersons)[number];
  supplierContactPerson: null;
  adminUnit: (typeof adminUnits)[number];
  logo: null;
  isActive: boolean;
  suppliedBy: null;
  readyForExport: boolean;
  updatedAfterLastExport: boolean;
  lastUpdated: string;
  deadline: null;
  etd: null;
  erpItemId: null;
  defaultQty: number;
  nameGeneratorExtraText: null;
  physicalReferenceNumber: null;
  categories: never[];
  customFields: never[];
};

const lastUpdatedProbeItem = (
  id: number,
  name: string,
  lastUpdated: string
): LastUpdatedProbeItem => ({
  id,
  name,
  userDefinedId: `LUP-${id}`,
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated,
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
});

/** All 18 Last Updated boundary/time-of-day probe rows — pushed onto `itemsData` by `items.ts`. */
export const lastUpdatedProbeItems: LastUpdatedProbeItem[] = [
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearEndBeforeBoundary,
    'DL63212 LastUpdated Probe — Year-end before boundary',
    '2025-12-30T23:59:59'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearEndMidnight,
    'DL63212 LastUpdated Probe — Year-end midnight',
    '2025-12-31T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearEndMidday,
    'DL63212 LastUpdated Probe — Year-end midday',
    '2025-12-31T12:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearEndLateAndYearStartBefore,
    'DL63212 LastUpdated Probe — Year-end late (and just before year-start)',
    '2025-12-31T23:59:59'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearStartMidnightAndYearEndAfter,
    'DL63212 LastUpdated Probe — Year-start midnight (and just after year-end)',
    '2026-01-01T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearStartMidday,
    'DL63212 LastUpdated Probe — Year-start midday',
    '2026-01-01T12:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearStartLate,
    'DL63212 LastUpdated Probe — Year-start late',
    '2026-01-01T23:59:59'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.yearStartAfterBoundary,
    'DL63212 LastUpdated Probe — Year-start after boundary',
    '2026-01-02T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midRangeBefore,
    'DL63212 LastUpdated Probe — Mid-range before',
    '2026-02-07T23:59:59'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midRangeStart,
    'DL63212 LastUpdated Probe — Mid-range start',
    '2026-02-08T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midRangeMiddle,
    'DL63212 LastUpdated Probe — Mid-range middle',
    '2026-02-10T12:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midDayEarly,
    'DL63212 LastUpdated Probe — Mid-day early',
    '2026-02-10T00:00:01'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midDayLate,
    'DL63212 LastUpdated Probe — Mid-day late',
    '2026-02-10T23:59:59'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midRangeEnd,
    'DL63212 LastUpdated Probe — Mid-range end',
    '2026-02-12T23:59:59'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midRangeAfter,
    'DL63212 LastUpdated Probe — Mid-range after',
    '2026-02-13T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.midDayAfter,
    'DL63212 LastUpdated Probe — Mid-day after',
    '2026-02-11T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.farPast,
    'DL63212 LastUpdated Probe — Far past',
    '2020-01-01T00:00:00'
  ),
  lastUpdatedProbeItem(
    lastUpdatedProbeIds.farFuture,
    'DL63212 LastUpdated Probe — Far future',
    '2030-01-01T00:00:00'
  ),
];
